/* Copyright (c) 2023-2025 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { BatchAction, useUpdatePlanMutation } from "~/generated/graphql";
import { showError } from "~/services/toast";
import { usePlan } from "~/views/plans/PlanDetails/PlanProvider";

type Props = {
  onSuccess: () => void;
};

type UpdatePlanEntityNameInput = {
  id?: string;
  batchId?: string;
  batchName?: string;
  fallbackName?: string;
  value?: {
    name: string;
  };
};

export function useUpdatePlanEntityName({ onSuccess }: Props) {
  const { plan } = usePlan();

  const [updatePlanMutation, { loading }] = useUpdatePlanMutation({
    onCompleted: (data) => {
      if (data.updatePlan) {
        onSuccess();
      }
    },
    onError: (error) => {
      showError(error.message);
    },
    update(cache, { data }, { variables }) {
      if (data?.updatePlan) {
        if (variables?.input.planActions) {
          const planActions = variables.input.planActions;
          cache.modify({
            id: cache.identify({
              __typename: "PlanAction",
              id: planActions[0].id,
            }),
            fields: {
              name: () => `${planActions[0].name}`,
            },
          });
        } else if (variables?.input.planActionBatches) {
          const batch = variables.input.planActionBatches[0];

          if (!batch.batchActions) {
            cache.modify({
              id: cache.identify({
                __typename: "PlanActionBatch",
                id: batch.id,
              }),
              fields: {
                name: () => `${batch.name}`,
              },
            });
          } else {
            const batchAction = batch.batchActions[0];

            cache.modify({
              id: cache.identify({
                __typename: "BatchAction",
                id: batchAction.id,
              }),
              fields: {
                batchActions: (existingBatchActions) => {
                  return existingBatchActions?.map((action: BatchAction) =>
                    action.id === batchAction.id
                      ? { ...action, name: batchAction.name }
                      : action
                  );
                },
              },
            });
          }
        }
        cache.gc();
      }
    },
  });

  function updatePlanEntityName(data: UpdatePlanEntityNameInput) {
    if (!plan) {
      return;
    }

    const providedName = data.value?.name?.trim() ?? "";
    const fallbackName = data.fallbackName ?? "";
    const finalName = providedName.length === 0 ? fallbackName : providedName;

    updatePlanMutation({
      variables: {
        input: {
          id: `${plan.id}`,
          ...createEntityNameInput({
            ...data,
            value: { name: finalName },
          }),
        },
      },
    });
  }

  return [updatePlanEntityName, loading] as const;
}

const createEntityNameInput = (inputData: UpdatePlanEntityNameInput) => {
  if (inputData.batchName) {
    return createBatchNameInput(inputData);
  }

  if (inputData.batchId) {
    return createBatchActionNameInput(inputData);
  }

  return createPlanActionNameInput(inputData);
};

const createBatchNameInput = (inputData: UpdatePlanEntityNameInput) => ({
  planActionBatches: [
    {
      id: inputData.batchId,
      name: inputData.value?.name,
    },
  ],
});

const createBatchActionNameInput = (inputData: UpdatePlanEntityNameInput) => ({
  planActionBatches: [
    {
      id: inputData.batchId,
      batchActions: [
        {
          id: inputData.id,
          name: inputData.value?.name,
        },
      ],
    },
  ],
});

const createPlanActionNameInput = (inputData: UpdatePlanEntityNameInput) => ({
  planActions: [
    {
      id: inputData.id,
      name: inputData.value?.name,
    },
  ],
});
